import {
  useApiExplorerApiResponse,
  useApiExplorerEndpoint,
} from '@squareup/dex-ui-shared-api-explorer-providers';
import { useToaster } from '@squareup/dex-ui-shared-market';
import { lookupPrefill } from '@squareup/dex-utils-shared-api-explorer';
import { useSearchParamsObject } from '@squareup/dex-utils-shared-routing-hooks';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const strings = {
  requiredFieldsPopulated: 'Required fields have been populated',
  requestReceived: 'Your request has been received!',
  returnToDashboard: 'Return to Dashboard',
};

const usePrefillDataToast = () => {
  const { appId, prefill } = useSearchParamsObject();

  const { apiResponse, isApiRequestRunning } = useApiExplorerApiResponse();
  const endpoint = useApiExplorerEndpoint();

  const { showToast } = useToaster();
  const shownToast = useRef(false);

  const hasPrefill = useMemo(
    () => Boolean(lookupPrefill(prefill || '', endpoint?.id || '', undefined)),
    [endpoint?.id, prefill]
  );

  const showAttnMarker = useMemo(() => {
    const status = apiResponse?.status;
    if (
      !hasPrefill ||
      isApiRequestRunning ||
      (status && status >= 200 && status <= 299)
    ) {
      return false;
    }
    return true;
  }, [apiResponse, hasPrefill, isApiRequestRunning]);

  useEffect(() => {
    // `prefill` is a query param that can be used to prefill the a request with required data values
    // This is used in the tutorial flow from connect-web/devs-console
    if (hasPrefill && !shownToast.current && !apiResponse?.status) {
      shownToast.current = true;
      showToast({
        message: strings.requiredFieldsPopulated,
        variant: 'info',
        isPersistent: true,
      });
    }
  }, [hasPrefill, apiResponse?.status, showToast, appId]);

  return { showAttnMarker };
};

const usePrefillSuccessToast = () => {
  const { appId, prefill } = useSearchParamsObject();
  const endpoint = useApiExplorerEndpoint();

  const shownToast = useRef(false);

  const { showToast } = useToaster();

  const hasPrefill = useMemo(
    () => Boolean(lookupPrefill(prefill || '', endpoint?.id || '', undefined)),
    [endpoint?.id, prefill]
  );

  const tryShowPrefillSuccessToast = useCallback(
    (status: number | undefined) => {
      if (hasPrefill && !shownToast.current && status) {
        if (status && status >= 200 && status <= 299) {
          shownToast.current = true;
          showToast({
            message: strings.requestReceived,
            variant: 'success',
            isPersistent: true,
            linkText: strings.returnToDashboard,
            href: `/apps${
              appId ? `/${appId}` : ''
            }?completeTask=createApiRequest`,
          });
        }
      }
    },
    [appId, hasPrefill, showToast]
  );

  return { tryShowPrefillSuccessToast };
};

export { usePrefillDataToast, usePrefillSuccessToast };
